import type { KyInstance } from 'ky';

import { convertToQueryString, ensureError, flattenFilterConditions, isDefined } from '@enterprise/core';
import type { OperationResultWithData, PagedData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { PagedRecipientPreviewQuery } from './types/paged-recipient-preview-query.type';
import type { PagedRecipientPreview } from './types/paged-recipient-preview.type';

/**
 * Fetches a paged list of recipient previews from the API.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param searchParams - The query parameters for paging and filtering recipient previews.
 * @returns A promise that resolves to an operation result containing paged recipient preview data.
 * @throws Will throw an error if the request fails.
 */
export const getPagedRecipientPreview = async (instance: KyInstance, searchParams: PagedRecipientPreviewQuery) => {
  try {
    const { filter, sort, ...restParams } = searchParams;
    const flattenedFilters = isDefined(filter) ? flattenFilterConditions(filter) : [];

    return await instance
      .get<OperationResultWithData<PagedData<PagedRecipientPreview>>>(Uri.recipient.getPagedPreview, {
        searchParams: {
          ...restParams,
          ...convertToQueryString(flattenedFilters, 'filter'),
          ...convertToQueryString(sort, 'sort'),
        },
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
